export type OidcSessionInvalidationReason =
    | 'user-unloaded'
    | 'user-signed-out'
    | 'silent-renew-error'
    | 'refresh-failed';

export interface OidcSessionEventSource {
    addUserUnloaded(listener: () => void): void;
    addSilentRenewError(listener: (error: Error) => void): void;
    addAccessTokenExpired(listener: () => void): void;
}

export function bindOidcSessionInvalidationEvents(
    events: OidcSessionEventSource,
    refresh: () => Promise<unknown>,
    invalidate: (reason: OidcSessionInvalidationReason) => void | Promise<void>,
): void {
    const safelyInvalidate = (reason: OidcSessionInvalidationReason) => {
        try {
            void Promise.resolve(invalidate(reason)).catch(() => undefined);
        } catch {
            // Authentication is already treated as invalid; event dispatch must
            // not produce an unhandled error if a UI subscriber fails.
        }
    };

    events.addUserUnloaded(() => {
        safelyInvalidate('user-unloaded');
    });
    events.addSilentRenewError(() => {
        safelyInvalidate('silent-renew-error');
    });
    events.addAccessTokenExpired(() => {
        void Promise.resolve().then(refresh).catch(() => undefined);
    });
}

export interface OidcSessionInvalidationSource {
    onSessionInvalidated(
        listener: (reason: OidcSessionInvalidationReason) => void,
    ): () => void;
}

export function bindOidcSessionInvalidationToStore(
    source: OidcSessionInvalidationSource,
    dispatch: (action: unknown) => unknown,
    logoutAction: () => unknown,
    afterInvalidation?: () => void,
): () => void {
    return source.onSessionInvalidated(() => {
        try {
            dispatch(logoutAction());
        } finally {
            afterInvalidation?.();
        }
    });
}
